import { ButtonBase, Skeleton, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { extractKeyValuePairs } from "encamp-omnisearch/src/utils/util";
import { useOmnisearch } from "providers/OmnisearchDataContext";
import { useParams } from "react-router-dom";

import { useTaskCountQueries } from "./useTaskCountQueries";

const STATUS_KEYS = ["status", "taskStatus"] as const;

/**
 * Replace any existing `status`/`taskStatus` token with a single new
 * key:value (or remove both if `nextKey` is null). Returns the rebuilt
 * omnisearch string. The status filter buttons are mutually exclusive, so
 * we compute the next state atomically rather than via two sequential
 * mutations (which both read a stale closure of the current value).
 */
function replaceStatusFilter(
  omnisearch: string,
  nextKey: "status" | "taskStatus" | null,
  nextValue: string | null
): string {
  const pairs = extractKeyValuePairs(omnisearch);
  const tokens: string[] = [];
  for (const pair of pairs) {
    if ((STATUS_KEYS as readonly string[]).includes(pair.key)) continue;
    tokens.push(omnisearch.slice(pair.start, pair.end));
  }
  if (nextKey && nextValue) {
    tokens.push(`${nextKey}:${nextValue}`);
  }
  return tokens.join(" ").trim();
}

export enum TaskFilterType {
  Open = "open",
  Overdue = "overdue",
  Suggested = "suggested",
  Completed = "completed",
  AllButCanceled = "allButCanceled",
}

interface TaskStatCardProps {
  icon: string;
  label: string;
  count: number | undefined;
  loading: boolean;
  onClick: () => void;
}

const TaskStatCard = ({ icon, label, count, loading, onClick }: TaskStatCardProps) => {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        width: 200,
        height: 52,
        px: 2,
        borderRadius: 1,
        backgroundColor: grey[50],
        "&:hover": {
          backgroundColor: grey[100],
        },
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, mb: "2px" }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width={60} height={20} />
      ) : (
        <Typography variant="body2" fontWeight="bold">
          {icon} {count ?? 0}
        </Typography>
      )}
    </ButtonBase>
  );
};

export const TaskFilterChips = ({ facilityId }: { facilityId?: string } = {}) => {
  const { tenantId } = useParams();
  const { omnisearch, setOmnisearchImmediate } = useOmnisearch();

  const { allQuery, overdueQuery, completedQuery } = useTaskCountQueries({
    tenantId,
    facilityId,
  });

  const applyStatusFilter = (key: "status" | "taskStatus" | null, value: string | null) => {
    setOmnisearchImmediate(replaceStatusFilter(omnisearch, key, value));
  };

  return (
    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
      <TaskStatCard
        icon="📋"
        label="All Tasks"
        count={allQuery.data?.virtualEvents.count}
        loading={allQuery.loading}
        onClick={() => applyStatusFilter(null, null)}
      />
      <TaskStatCard
        icon="⏰"
        label="Overdue Tasks"
        count={overdueQuery.data?.virtualEvents.count}
        loading={overdueQuery.loading}
        onClick={() => applyStatusFilter("status", "OVERDUE")}
      />
      <TaskStatCard
        icon="✅"
        label="Completed Tasks"
        count={completedQuery.data?.virtualEvents.count}
        loading={completedQuery.loading}
        onClick={() => applyStatusFilter("taskStatus", "COMPLETED")}
      />
    </Stack>
  );
};
